import { AlbumNode, CanvasConfig } from "@/engine/album";
import { eventAlbumConstants as C } from "../actions/constants/eventAlbumConstants";
import { PageHistory } from "@/types/history";
export interface EventAlbumPage {
  pageId: string;
  pageNumber: number;
  label: string;
  nodes: AlbumNode[];
  backgroundColor: string;
  backgroundAssetId: string | null;
  backgroundOpacity: number;
  metadata: {
    createdAt: number;
    updatedAt: number;
    version: number;
  };
  history:PageHistory[]
}

export interface EventAlbum {
  _id: string;
  eventId: string;
  studioId: string;
  serviceId: string;
  selectionId: string;
  templateId: string;
  pages: EventAlbumPage[];
  pageOrder: string[];
  status: "draft" | "ready" | "in_customer_review" | "approved" | "reopened";
  generationMeta: {
    generatedAt: string;
    totalPhotos: number;
    photosPlaced: number;
    overflowPhotos: number;
  };
  resolvedAssets: Record<string, string>;
  customerAccess: {
    accessCode: string | null;
    reviewToken: string | null;
    expiresAt: string | null;
    isActive: boolean;
    accessCount: number;
    validatedAt: string | null;
    invalidatedAt: string | null;
  };
  customerComment: string;
  studioNotes: string;
  reviewHistory: Array<{ action: string; actor: string; comment: string; timestamp: string }>;
  approvedAt: string | null;
  createdAt: string;
  updatedAt: string;
  photosRequiredCount: number;
  dimension: string;
  canvasConfig:CanvasConfig;
  numberOfSheets:number
  version:number;
}

export interface EventAlbumState {
  generating: boolean;
  generateError: string | null;

  album: EventAlbum | null;
  fetchLoading: boolean;
  fetchError: string | null;

  updateLoading: boolean;
  updateError: string | null;
  sendReviewLoading: boolean;
  sendReviewError: string | null;

  tokenViewLoading: boolean;
  tokenViewError: string | null;

  customerAlbum: EventAlbum | null;
  customerViewLoading: boolean;
  customerViewError: string | null;
  sessionReviewToken: string | null;

  approving: boolean;
  approveError: string | null;
}

const initialState: EventAlbumState = {
  generating: false,
  generateError: null,

  album: null,
  fetchLoading: false,
  fetchError: null,

  updateLoading: false,
  updateError: null,
  sendReviewLoading: false,
  sendReviewError: null,

  tokenViewLoading: false,
  tokenViewError: null,

  customerAlbum: null,
  customerViewLoading: false,
  customerViewError: null,

  sessionReviewToken: null,

  approving: false,
  approveError: null,
};

const eventAlbumReducer = (state = initialState, action: any): EventAlbumState => {
  switch (action.type) {

    case C.generateRequest:
      return { ...state, generating: true, generateError: null };
    case C.generateSuccess:
      return { ...state, generating: false, album: action.payload };
    case C.generateFailure:
      return { ...state, generating: false, generateError: action.payload };

    case C.fetchRequest:
    case C.fetchBySelectionRequest:
      return { ...state, fetchLoading: true, fetchError: null };
    case C.fetchSuccess:
    case C.fetchBySelectionSuccess:
      return { ...state, fetchLoading: false, album: action.payload };
    case C.fetchFailure:
    case C.fetchBySelectionFailure:
      return { ...state, fetchLoading: false, fetchError: action.payload };

    case C.updatePagesRequest:
      return { ...state, updateLoading: true, updateError: null };
    case C.updatePagesSuccess:
      return { ...state, updateLoading: false, album: action.payload };
    case C.updatePagesFailure:
      return { ...state, updateLoading: false, updateError: action.payload };

    case C.sendReviewRequest:
      return { ...state, sendReviewLoading: true, sendReviewError: null };
    case C.sendReviewSuccess: {
      const updatedAlbum = action.payload?.album ?? null;
      return {
        ...state,
        sendReviewLoading: false,
        album: updatedAlbum
          ? updatedAlbum
          : state.album
            ? { ...state.album, status: "in_customer_review" as const }
            : null,
      };
    }
    case C.sendReviewFailure:
      return { ...state, sendReviewLoading: false, sendReviewError: action.payload };


    case C.customerTokenViewRequest:
      return {
        ...state,
        tokenViewLoading: true,
        tokenViewError: null,
        sessionReviewToken: action.payload?.reviewToken ?? state.sessionReviewToken,
      };
    case C.customerTokenViewSuccess:
      return { ...state, tokenViewLoading: false, customerAlbum: action.payload };
    case C.customerTokenViewFailure:
      return { ...state, tokenViewLoading: false, tokenViewError: action.payload };

    case C.customerViewRequest:
      return { ...state, customerViewLoading: true, customerViewError: null };
    case C.customerViewSuccess:
      return { ...state, customerViewLoading: false, customerAlbum: action.payload };
    case C.customerViewFailure:
      return { ...state, customerViewLoading: false, customerViewError: action.payload };

    case C.customerApproveRequest:
      return { ...state, approving: true, approveError: null };
    case C.customerApproveSuccess:
      return {
        ...state,
        approving: false,
        customerAlbum: action.payload ?? (state.customerAlbum ? { ...state.customerAlbum, status: "approved" as const } : null),
        sessionReviewToken: null,
      };
    case C.customerApproveFailure:
      return { ...state, approving: false, approveError: action.payload };

    case C.reset:
      return initialState;

    default:
      return state;
  }
};

export default eventAlbumReducer;
